<template>
  <b-card header="Order Info">
    <b-row class="align-items-center">
      <b-col cols=12 xl=6>
        <dl>
          <dt>Order ID</dt>
          <dd class="big text-monospace">{{ order.num.toUpperCase() }}</dd>
          <dt>Shopify ID</dt>
          <dd><data-merger :dict=order.id /></dd>
          <dd>
            <external-btn label="Open Website Order" v-if=isWebOrder :href=orderViewUrl />
            <external-btn label="Open Website Return" v-if=isWebReturn :href=returnViewUrl />
            <external-btn label="Open Website Customer" v-if=customerExists :href=customerViewUrl />
            <external-btn label="Open Original Order" v-if=isWebReturn :href=orderViewUrl />
            <external-btn label="Show Customer Info" v-if=customerExists :href=customerInfoUrl />
          </dd>
          <b-row>
            <b-col v-if='order.websiteName'>
              <dt>{{order.websiteName | titleize}} Status</dt>
              <web-status :websiteOrder=websiteOrder class='big' />
            </b-col>
            <b-col v-else>
              <dt>Not on Website!!</dt>
            </b-col>
            <b-col>
              <dt>Fishbowl Status</dt>
              <fishbowl-status :fishbowl-order="fishbowlOrder" :to-import="toImport" :to-issue="toIssue" class="big" />
            </b-col>
          </b-row>
        </dl>
      </b-col>
      <b-col cols=12 md=4 xl=2>
        <dl v-if="isWebReturn">
          <dt>Total Refund</dt>
          <dd class='big text-monospace'>
            <b>{{refundTotal | formatDollars}}</b>
          </dd>
          <b-alert variant=danger :show="!!rmaWarning" class='p-1 mt-1 mb-0'>
            <span v-html=rmaWarning />
          </b-alert>
          <dt>Refunds</dt>
          <span v-if='refundedAmount > 0'>
            {{refundedAmount | formatDollars}} - <data-merger :dict=order.refundMethod title="Refund Method" />
          </span>
          <span v-else>
            N/A
          </span>
        </dl>
        <dl v-else>
          <dt>Total Price</dt>
          <dd class='big text-monospace'>
            <data-merger :dict=order.totalPrice formatter=formatDollars tag=b title="Total Price" />
          </dd>
          <span><em>{{paymentStatus}}</em></span>
          <dt>Payments</dt>
          <dd v-for="(payment,ix) in order.payments" :key=ix>
            <span>
              <data-merger :dict="payment.amount" formatter=formatDollars title="Payment Amount" />- <data-merger :dict="payment.method" :showWarning="false" title="Payment Method" />
              <div v-for="(refund,rix) in payment.transactions.filter(x => x.action.shopify == 'REFUND')" :key="`refund-${rix}`" class='ml-4'>
                → <data-merger :dict="refund.amount" formatter="formatDollars" title="Refund Amount" style='color:orangered' /> <em>(REFUNDED)</em>
              </div>
            </span>
            <span v-if='!!payment.displayNumber && isGCorPDV(payment)' class='nobr'>
              [ <a :href="getGCLink(payment)" target="_blank">{{getCCLast4(payment)}}</a>]
            </span>
            <span v-else-if='!!payment.displayNumber' class='nobr'>
              [ {{getCCLast4(payment)}} ]
            </span>
            <span v-else-if="!!payment.poNum" class='nobr'>
              [ <data-merger :dict="payment.poNum" title="PO Number" />]
            </span>
            <b-button variant=link style="color:orange" class='m-0 p-0' v-if="Object.keys(payment.method).length < 2" v-b-popover.hover="'This payment only exists in '+Object.keys(payment.method)+'!'">
              <i class="fas fa-exclamation-triangle"></i>
            </b-button>
            <b-button @click="showTransactions(ix)" variant='link' style="color:#ffc107" class='m-0 p-0' v-if="hasFailedTransactions(payment)" v-b-popover.hover="'This payment has failed transactions!'">
              <i class='fas fa-minus-circle'></i>
            </b-button>
            <b-modal :id="`transactions-${ix}`" :ref="`transactions-modal-${ix}`" hide-footer title='Transaction Details'>
              <data-merger :dict=payment.amount formatter=formatDollars title="Payment Amount" />- <data-merger :dict="payment.method" :showWarning="false" title="Payment Method" />
              <span v-if='!!payment.displayNumber && isGCorPDV(payment)' class='nobr'>
                [ <a :href="getGCLink(payment)" target="_blank">{{getCCLast4(payment)}}</a>]
              </span>
              <span v-else-if='!!payment.displayNumber' class='nobr'>
                [ {{getCCLast4(payment)}} ]
              </span>
              <span v-else-if="!!payment.poNum" class='nobr'>
                [ <data-merger :dict="payment.poNum" title="PO Number" />]
              </span>
              <ul>
                <li v-for="(transaction, tix) in payment.transactions" :key="`transaction-${tix}`">
                  <data-merger :dict="transaction.amount" formatter=formatDollars title="Transaction Amount" />
                  <data-merger :dict="transaction.action" title="Transaction Action" />
                  <data-merger :dict="transaction.status" title="Transaction Status" />
                </li>
              </ul>
            </b-modal>
          </dd>
          <a v-if="!!poPreviewLink" :href="poPreviewLink" target="_blank">PO Preview</a>
          <section v-if="!isWebReturn">
            <dt>Tax Exemption</dt>
            <dd :style="{ 'background-color': showTaxWarning ? '#dc3545' : 'none', 'color': showTaxWarning ? '#fff' : '#000' }">
              {{taxExemptStatus}}
              <span v-if=showTaxWarning v-b-popover.hover="'This user has active tax exemption!'">
                <i class='fas fa-exclamation-triangle'></i>
              </span>
            </dd>
          </section>
        </dl>
      </b-col>
      <b-col cols=12 md=4 xl=2>
        <dl>
          <dt>Email</dt>
          <dd>
            <data-merger :dict=order.email :copyButton=true title="Email" />
          </dd>
          <dt>Phone</dt>
          <dd>
            <data-merger :dict=order.phoneNumber formatter=formatPhone title="Phone Number" />
          </dd>
        </dl>
      </b-col>
      <b-col cols=12 md=4 xl=2 offset-lg=0>
        <dl>
          <div v-if=fishbowlOrder.createdAt>
            <dt>Order Created</dt>
            <nice-date :value=fishbowlOrder.createdAt />
          </div>
          <div v-if=fishbowlOrder.issuedAt>
            <dt>Order Issued</dt>
            <nice-date :value=fishbowlOrder.issuedAt />
          </div>
        </dl>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import FishbowlStatus from './FishbowlStatus.vue'
import WebStatus from './WebStatus.vue'
import ExternalBtn from '../../utility/ExternalButton.vue'
import DataMerger from '../../utility/DataMerger.vue';
import NiceDate from '../../utility/NiceDate.vue';
import orderMerge from '../../../services/orderMerge';
import commonService from '../../../helpers/services/common'

export default {
  props: {
    order: { type: Object, required: true },
    fishbowlOrder: { type: Object, required: true },
    websiteOrder: { type: Object, required: true },
    isWebReturn: { type: Boolean, default: false },
  },
  components: {
    FishbowlStatus,
    WebStatus,
    ExternalBtn,
    DataMerger,
    NiceDate,
  },
  data() {
    return {
      showTaxWarning: false,
    };
  },
  computed: {
    isWebOrder() {
      return !!this.order.websiteName
    },
    toImport() {
      return !orderMerge.mergeField(this.order.imported)
    },
    toIssue() {
      return this.toImport && orderMerge.mergeField(this.order.needIssued)
    },
    orderViewUrl() {
      return commonService.orderWebsiteLink(this.order)
    },
    returnViewUrl() {
      switch (this.order.websiteName) {
        case 'workarea':
          return `https://workarea.andymark.com/admin/returns/${this.order.num}`
        default:
          console.warn('!!! Fetching return viewing URL is not supported for this source!')
          return
      }
    },
    customerExists() {
      return !!orderMerge.mergeField(this.order.customerId)
    },
    customerViewUrl() {
      let customerId = orderMerge.mergeField(this.order.customerId)
      switch (this.order.websiteName) {
        case 'workarea':
          return `https://workarea.andymark.com/admin/users/${customerId}`
        case 'shopify':
          return `https://admin.shopify.com/store/2ca7d3-42/customers/${customerId}`
        default:
          console.warn('!!! Fetching customer viewing URL is not supported for this source!')
          return
      }
    },
    customerInfoUrl() {
      let customerId = orderMerge.mergeField(this.order.customerId)
      switch (this.order.websiteName) {
        case 'shopify':
          return `/customers?id=${this.order.customerId.shopify}`;
        default:
          console.warn('!!! Fetching customer INFO URL is not supported for this source!')
          return
      }
    },
    rmaWarning() {
      let methods = orderMerge.mergeField(this.order.refundMethod)

      if (this.order.websiteName !== 'workarea') {
        return `This RMA is NOT A WORKAREA RMA.<br/>
        We have no idea how these work still :)`
      } else if (this.order.status[this.order.websiteName] === 'doesnt_exist') {
        return `This RMA is only in Fishbowl.<br/>
        It will not be refunded automatically!`
      } else if (Array.isArray(methods) && methods.length > 1) {
        return `This RMA has multiple refund methods.<br/>
        It will not be refunded automatically!`
      } else if (this.order.websiteName == 'workarea' && this.order.refundMethod.workarea === '???') {
        return `This RMA is non-CC/GC/PDV.<br/>
        It will not be refunded automatically!`
      } else {
        return undefined
      }
    },
    refundTotal() {
      return -1 * orderMerge.mergeField(this.order.totalPrice)
    },
    refundedAmount() {
      return orderMerge.mergeField(this.order.refundedAmount)
    },
    paymentStatus() {
      if (this.order.websiteName === 'shopify') {
        if (this.order.num.startsWith('D')) return 'DRAFT'
        return this.order.paymentStatus.shopify;
      }

      let statuses = this.order.payments.map(payment => {
        if (!payment.transactions) return []
        console.log(payment.transactions)
        return payment.transactions.map(t => orderMerge.mergeField(t.action))
      }).flat()
      if (statuses.includes('refund')) {
        let refundedAmount = this.sumTransactions('refund')
        let capturedAmount = this.sumTransactions('capture') + this.sumTransactions('purchase')
        if (refundedAmount < capturedAmount) {
          return 'Partially Refunded'
        } else {
          return 'Refunded'
        }
      } else if (statuses.includes("capture") || statuses.includes('purchase')) {
        return 'Captured'
      } else if (statuses.includes('authorize')) {
        return 'Authorized'
      } else return "🤷‍♀️"
    },
    taxExemptStatus() {
      let hasTax = orderMerge.mergeField(this.order.taxTotal) > 0
      let hasForm = !!orderMerge.mergeField(this.order.taxExemptFormId) ||
                    orderMerge.mergeField(this.order.customerIsTaxExempt) ||
                    (orderMerge.mergeField(this.order.customerTaxExemptions) || []).length > 0
      if (hasForm) {
        if (hasTax) {
          this.showTaxWarning = true
          return 'Exempt, but Taxed!'
        } else {
          this.showTaxWarning = false
          return 'Tax Exempt'
        }
      } else {
        if (hasTax) {
          this.showTaxWarning = false
          return 'Not Exempt'
        } else {
          this.showTaxWarning = false
          return 'Not Exempt, but no Tax!'
        }
      }
    },
    poPreviewLink() {
      if (this.order.websiteName !== 'shopify' || !Array.isArray(this.order.metafields)) return null;

      const filter = this.order.metafields.filter(x => x.namespace.shopify=="order" && x.key.shopify=="po_file")
      return filter.length > 0 ? filter[0].references[0].url.shopify : null;
    },
  },
  methods: {
    sumTransactions(status) {
      return this.order.payments.filter(payment => {
        return !!payment.transactions && payment.transactions.length > 0
      }).map(payment => {
        return payment.transactions.filter(t => {
          return orderMerge.mergeField(t.action) == status
        }).map(t => {
          return orderMerge.mergeField(t.amount)
        })
      }).flat().reduce((m, x) => m += x, 0)
    },
    isGCorPDV(payment) {
      let method = orderMerge.mergeField(payment.method)
      return method == "Gift Card" || method == "PDV"
    },
    getGCLink(payment) {
      let token = orderMerge.mergeField(payment.token)
      switch(this.order.websiteName) {
        case 'workarea':
          return `https://workarea.andymark.com/admin/payment_gift_cards?utf8=%E2%9C%93&sort=relevance&q=${token}&created_at_greater_than=&created_at_less_than=&quick_range=a+range&commit=search_gift_cards`
        case 'shopify':
          const giftCardId = orderMerge.mergeField(payment.giftCardId)
          return `https://admin.shopify.com/store/andymarkinc/gift_cards/${giftCardId}`
        default:
          console.warn(`!!! fetching GC link is not supported for ${this.order.websiteName}`)
      }
    },
    getCCLast4(payment) {
      let displayNumber = orderMerge.mergeField(payment.displayNumber)
      return displayNumber.split(' ')[3]
    },
    hasFailedTransactions(payment) {
      return payment.transactions.some(t => orderMerge.mergeField(t.status) === 'FAILURE')
    },
    showTransactions(ix) {
      this.$refs[`transactions-modal-${ix}`][0].show()
    },
  },
}
</script>

<style scoped>
dt {
  margin-top: 0.66rem;
}
dl {
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/sales-order/view-order-2/BasicInfo.vue